import { ChatMsg } from "../interfaces/ChatMsg";
import { PreloadedAssets } from "../interfaces/PreloadedAssets";
import {
  resolveAndPreloadImage,
  resolveAndPreloadImageSequential,
  resolveAndPreloadAudio,
  getAnimDuration,
} from "../../utils/assetCache";
import transparentPng from "../../constants/transparentPng";
import {
  buildEmoteCandidatesWithFallback,
  buildBareCandidates,
} from "../../utils/assetPaths";
import { getThemePlaceholderUrls } from "../../webcoa/config";

const GLOBAL_TIMEOUT_MS = 8000;

/**
 * Builds the ordered candidate URL list for a character emote.
 *
 * webCOA fix: routes through the authoritative resolver, which tries
 * BOTH the leaf-prefix form ("(a)normal") AND the directory-prefix form
 * ("(a)/normal") for every emote, with the desktop ext order and the
 * placeholder fallback chain. The `prefix` argument selects idle vs talk
 * vs preanim:
 *   "(a)" -> idle,  "(b)" -> talk,  ""  -> preanim (bare name, no prefix).
 */
function buildEmoteUrls(
  AO_HOST: string,
  _extensions: string[],
  charactername: string,
  emotename: string,
  prefix: string,
): string[] {
  let placeholders: string[] | undefined;
  try {
    placeholders = getThemePlaceholderUrls();
  } catch {
    placeholders = undefined;
  }
  if (prefix === "(b)") {
    return buildEmoteCandidatesWithFallback(AO_HOST, charactername, emotename, {
      talking: true,
      caseFallback: false,
      themePlaceholderUrls: placeholders,
    });
  }
  if (prefix === "(a)") {
    return buildEmoteCandidatesWithFallback(AO_HOST, charactername, emotename, {
      talking: false,
      caseFallback: false,
      themePlaceholderUrls: placeholders,
    });
  }
  // No prefix -> preanim: bare name, no (a)/(b) and no placeholder (a
  // missing preanim should fall through to the transparent pixel).
  return buildBareCandidates(AO_HOST, charactername, emotename, {
    caseFallback: false,
  });
}

const DEFAULT_ASSETS: PreloadedAssets = {
  idleUrl: transparentPng,
  talkingUrl: transparentPng,
  preanimUrl: transparentPng,
  preanimDuration: 0,
  pairIdleUrl: transparentPng,
  shoutSfxUrl: null,
  emoteSfxUrl: null,
  realizationSfxUrl: null,
  stabSfxUrl: null,
};

/**
 * Preloads all assets referenced in an IC message before the animation timeline starts.
 * Resolves all file extensions in parallel and primes the browser cache.
 * All resolution and preloading is cached via assetCache, so repeated messages
 * from the same character with the same emotes resolve near-instantly.
 */
export default async function preloadMessageAssets(
  chatmsg: ChatMsg,
  AO_HOST: string,
  emoteExtensions: string[],
): Promise<PreloadedAssets> {
  const charName = chatmsg.name!.toLowerCase();
  const charEmote = chatmsg.sprite!.toLowerCase();

  const doPreload = async (): Promise<PreloadedAssets> => {
    // Build candidate URL lists for each emote
    const idleUrls = buildEmoteUrls(AO_HOST, emoteExtensions, charName, charEmote, "(a)");
    const talkingUrls = buildEmoteUrls(AO_HOST, emoteExtensions, charName, charEmote, "(b)");

    // A preanim is present for emote_mod 1/6, OR for any immediate
    // (non-interrupting) message -- AO2 immediate messages carry a preanim
    // that plays concurrently with the text regardless of the emote_mod, and
    // gating purely on type 1/6 left their preanimDuration at 0, which made
    // the viewport skip the preanim entirely.
    const hasPreanim =
      (chatmsg.type === 1 ||
        chatmsg.type === 6 ||
        chatmsg.noninterrupting_preanim === 1) &&
      chatmsg.preanim &&
      chatmsg.preanim !== "-" &&
      chatmsg.preanim !== "";

    // Normalize the preanim name: strip leading slashes some char.inis
    // prepend (e.g. Lana's "/anim/turningforward"). Without this the
    // duration URL became ".../lana//anim/turningforward" (double slash),
    // 404'd, returned 0, and the preanim was cut short -- it looked too
    // fast. buildBareCandidates already strips it for the image itself.
    const preanimName = hasPreanim
      ? chatmsg.preanim!.toLowerCase().replace(/^\/+/, "")
      : "";

    const preanimUrls = hasPreanim
      ? buildEmoteUrls(AO_HOST, emoteExtensions, charName, preanimName, "")
      : null;

    const hasPair = !!chatmsg.other_name;
    const pairIdleUrls = hasPair
      ? buildEmoteUrls(AO_HOST, emoteExtensions, chatmsg.other_name!.toLowerCase(), chatmsg.other_emote!.toLowerCase(), "(a)")
      : null;

    // Shout SFX per-character path
    const shoutNames = [undefined, "holdit", "objection", "takethat", "custom"];
    const shoutName = shoutNames[chatmsg.objection];
    const shoutSfxPath = (chatmsg.objection > 0 && chatmsg.objection < 4 && shoutName)
      ? `${AO_HOST}characters/${encodeURI(charName)}/${shoutName}.opus`
      : null;

    // Emote SFX
    const invalidSounds = ["0", "1", "", undefined];
    const emoteSfxPath = (
      !invalidSounds.includes(chatmsg.sound) &&
      (chatmsg.type == 1 || chatmsg.type == 2 || chatmsg.type == 5 || chatmsg.type == 6)
    ) ? `${AO_HOST}sounds/general/${encodeURI(chatmsg.sound.toLowerCase())}.opus`
      : null;

    // Realization and stab SFX (always preloaded - used by \f and \s text commands)
    const realizationPath = `${AO_HOST}sounds/general/sfx-realization.opus`;
    const stabPath = `${AO_HOST}sounds/general/sfx-stab.opus`;

    // Launch everything in parallel - assetCache handles per-URL dedup and caching
    const [
      idleUrl, talkingUrl, preanimUrl, preanimDuration, pairIdleUrl,
      shoutSfxUrl, emoteSfxUrl, realizationSfxUrl, stabSfxUrl,
    ] = await Promise.all([
      resolveAndPreloadImageSequential(idleUrls),
      resolveAndPreloadImageSequential(talkingUrls),
      preanimUrls ? resolveAndPreloadImageSequential(preanimUrls) : Promise.resolve(transparentPng),
      hasPreanim
        ? getAnimDuration(`${AO_HOST}characters/${encodeURI(charName)}/${encodeURI(preanimName)}`)
        : Promise.resolve(0),
      pairIdleUrls ? resolveAndPreloadImageSequential(pairIdleUrls) : Promise.resolve(transparentPng),
      shoutSfxPath ? resolveAndPreloadAudio(shoutSfxPath) : Promise.resolve(null),
      emoteSfxPath ? resolveAndPreloadAudio(emoteSfxPath) : Promise.resolve(null),
      resolveAndPreloadAudio(realizationPath),
      resolveAndPreloadAudio(stabPath),
    ]);

    return {
      idleUrl,
      talkingUrl,
      preanimUrl,
      preanimDuration,
      pairIdleUrl,
      shoutSfxUrl,
      emoteSfxUrl,
      realizationSfxUrl,
      stabSfxUrl,
    };
  };

  // Race against global timeout for graceful degradation
  const timeoutPromise = new Promise<PreloadedAssets>((resolve) => {
    setTimeout(() => {
      console.warn("Asset preloading timed out, using defaults");
      resolve({ ...DEFAULT_ASSETS });
    }, GLOBAL_TIMEOUT_MS);
  });

  try {
    return await Promise.race([doPreload(), timeoutPromise]);
  } catch (error) {
    console.error("Asset preloading failed:", error);
    return { ...DEFAULT_ASSETS };
  }
}
